import React, { useEffect, useRef, useState } from 'react';
import { UploadApi } from '../../../api/adminApi';
import './RichTextEditor.css';

const WANG_EDITOR_VERSION = '5.1.23';
const WANG_EDITOR_CSS = `https://unpkg.com/@wangeditor/editor@${WANG_EDITOR_VERSION}/dist/css/style.css`;
const WANG_EDITOR_SRC = `https://unpkg.com/@wangeditor/editor@${WANG_EDITOR_VERSION}/dist/index.js`;

const FONT_SIZE_OPTIONS = [
  '10px',
  '11px',
  '12px',
  '13px',
  '14px',
  '15px',
  '16px',
  '17px',
  '18px',
  '20px',
  '22px',
  '24px',
  '26px',
  '28px',
  '30px',
  '32px',
  '36px',
  '40px',
  '42px',
  '48px',
  '54px',
  '60px',
  '72px'
];
const FONT_FAMILY_OPTIONS = [
  'Inter, -apple-system, BlinkMacSystemFont, Segoe UI, sans-serif',
  'Gellix, Helvetica Neue, Arial, sans-serif',
  'Helvetica, Helvetica Neue, Arial, sans-serif',
  'Arial, Helvetica, sans-serif',
  'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  'Georgia, Times New Roman, Times, serif',
  'Merriweather, Georgia, serif',
  'Courier New, Courier, monospace',
  'SFMono-Regular, Consolas, Liberation Mono, Menlo, monospace'
];

let wangEditorPromise = null;
let wangStylesLoaded = false;

const injectStylesheet = () => {
  if (typeof document === 'undefined' || wangStylesLoaded) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('link[data-wangeditor="stylesheet"]');
    if (existing) {
      wangStylesLoaded = true;
      resolve();
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = WANG_EDITOR_CSS;
    link.dataset.wangeditor = 'stylesheet';
    link.onload = () => {
      wangStylesLoaded = true;
      resolve();
    };
    link.onerror = () => reject(new Error('Unable to load wangEditor styles')); 
    document.head.appendChild(link);
  });
};

const loadScript = (src, dataAttr) => new Promise((resolve, reject) => {
  const existing = document.querySelector(`script[data-wangeditor="${dataAttr}"]`);
  if (existing) {
    existing.addEventListener('load', resolve);
    existing.addEventListener('error', reject);
    if (existing.dataset.loaded === 'true') {
      resolve();
    }
    return;
  }
  const script = document.createElement('script');
  script.src = src;
  script.async = true;
  script.dataset.wangeditor = dataAttr;
  script.onload = () => {
    script.dataset.loaded = 'true';
    resolve();
  };
  script.onerror = () => reject(new Error(`Failed to load ${src}`));
  document.head.appendChild(script);
});

const loadWangEditor = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Window is undefined'));
  }
  if (window.wangEditor) {
    return Promise.resolve(window.wangEditor);
  }
  if (wangEditorPromise) {
    return wangEditorPromise;
  }
  wangEditorPromise = injectStylesheet()
    .then(() => loadScript(WANG_EDITOR_SRC, 'core'))
    .then(() => {
      if (!window.wangEditor) {
        throw new Error('wangEditor unavailable after load');
      }
      return window.wangEditor;
    })
    .catch((error) => {
      wangEditorPromise = null;
      throw error;
    });
  return wangEditorPromise;
};

const RichTextEditor = ({ value = '', onChange, placeholder = 'Write blog content…', helperText }) => {
  const editorContainerRef = useRef(null);
  const toolbarContainerRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const toolbarInstanceRef = useRef(null);
  const latestValueRef = useRef(value || '');
  const lastEditorValueRef = useRef(value || '');
  const onChangeRef = useRef(onChange);
  const modeRef = useRef('visual');

  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [mode, setMode] = useState('visual');
  const [htmlValue, setHtmlValue] = useState(value || '');

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  useEffect(() => {
    latestValueRef.current = value || '';
  }, [value]);

  useEffect(() => {
    let isMounted = true;

    loadWangEditor()
      .then((wangEditor) => {
        if (!isMounted || !editorContainerRef.current) return null;
        const { createEditor, createToolbar, i18nChangeLanguage } = wangEditor;
        i18nChangeLanguage?.('en');

        const editor = createEditor({
          selector: editorContainerRef.current,
          html: latestValueRef.current,
          config: {
            placeholder,
            onChange(ed) {
              const html = ed.getHtml();
              lastEditorValueRef.current = html;
              if (typeof onChangeRef.current === 'function' && modeRef.current === 'visual') {
                onChangeRef.current(html);
              }
            },
            MENU_CONF: {
              fontSize: {
                fontSizeList: FONT_SIZE_OPTIONS
              },
              fontFamily: {
                fontFamilyList: FONT_FAMILY_OPTIONS
              },
              uploadImage: {
                async customUpload(file, insertFn) {
                  const { url } = await UploadApi.uploadImage(file);
                  insertFn(url);
                }
              },
              uploadVideo: {
                async customUpload(file, insertFn) {
                  const { url } = await UploadApi.uploadImage(file);
                  insertFn(url);
                }
              }
            }
          },
          mode: 'default'
        });

        editorInstanceRef.current = editor;

        if (toolbarContainerRef.current) {
          toolbarInstanceRef.current = createToolbar({
            editor,
            selector: toolbarContainerRef.current,
            mode: 'default'
          });
        }

        setStatus('ready');
        setError('');
        return null;
      })
      .catch((err) => {
        if (!isMounted) return;
        setStatus('error');
        setError(err.message || 'Failed to load editor');
      });

    return () => {
      isMounted = false;
      if (toolbarInstanceRef.current) {
        toolbarInstanceRef.current.destroy?.();
        toolbarInstanceRef.current = null;
      }
      if (editorInstanceRef.current) {
        editorInstanceRef.current.destroy?.();
        editorInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (editorInstanceRef.current) {
      editorInstanceRef.current.setPlaceholder?.(placeholder || '');
    }
  }, [placeholder]);

  useEffect(() => {
    if (mode === 'html' && value !== undefined && value !== null && value !== htmlValue) {
      setHtmlValue(value);
    }
  }, [mode, value, htmlValue]);

  useEffect(() => {
    if (!editorInstanceRef.current) return;
    const editor = editorInstanceRef.current;
    const currentHtml = editor.getHtml();
    if (
      mode === 'visual' &&
      value !== undefined &&
      value !== null &&
      value !== lastEditorValueRef.current
    ) {
      editor.setHtml(value || '');
      lastEditorValueRef.current = value || '';
    }
  }, [value, mode]);

  const handleModeChange = (nextMode) => {
    if (nextMode === mode || status !== 'ready') return;
    if (nextMode === 'html') {
      const currentHtml = editorInstanceRef.current ? editorInstanceRef.current.getHtml() : value;
      setHtmlValue(currentHtml || '');
      setMode('html');
    } else {
      if (editorInstanceRef.current) {
        editorInstanceRef.current.setHtml(htmlValue || '');
      }
      setMode('visual');
    }
  };

  const handleHtmlInput = (event) => {
    const nextValue = event.target.value;
    setHtmlValue(nextValue);
    lastEditorValueRef.current = nextValue;
    if (typeof onChange === 'function') {
      onChange(nextValue);
    }
  };

  return (
    <div className="rich-text-editor">
      <div className="editor-mode-toggle">
        <button
          type="button"
          className={mode === 'visual' ? 'active' : ''}
          onClick={() => handleModeChange('visual')}
          disabled={status !== 'ready'}
        >
          Visual Editor
        </button>
        <button
          type="button"
          className={mode === 'html' ? 'active' : ''}
          onClick={() => handleModeChange('html')}
        >
          HTML Source
        </button>
      </div>

      <div className="wang-editor-host">
        {status === 'loading' && <div className="editor-loading">Loading editor…</div>}
        {status === 'error' && <div className="editor-error">{error}</div>}

        <div className={`wang-toolbar ${mode !== 'visual' || status !== 'ready' ? 'is-hidden' : ''}`} ref={toolbarContainerRef} />
        <div className={`wang-editor-container ${mode !== 'visual' || status !== 'ready' ? 'is-hidden' : ''}`} ref={editorContainerRef} />

        {mode === 'html' && (
          <textarea
            className="html-editor-textarea"
            value={htmlValue}
            onChange={handleHtmlInput}
            placeholder="Paste or write HTML markup here"
          />
        )}
      </div>

      {helperText && <p className="helper-text">{helperText}</p>}
    </div>
  );
};

export default RichTextEditor;
